/**
 * This will work as a default swiper where depending on the breakpoints, the content will overflow the container.
 *
 * Be sure to include the components-default-swiper.css and add the default-swiper class to the section.
 * Also be sure to include the slide classes from _cls.scss, so swiper-container--2-slides, swiper-container--md-3-slides, etc.
 * When using custom pagination + navigation, add 'swiper swiper--custom-navigation' classes to the swiper.
 * When the swiper should be screen width, use default-swiper--full.
 *
 * Html markup:

Header with nav icons
  <div class="default-swiper">
    <div class="default-swiper__header is-flex is-justified-space-between">
      <div>
        {%- if section.settings.title != blank -%}
          <h2 class="h3 default-swiper__title">{{- section.settings.title -}}</h2>
        {%- endif -%}
        {%- if section.settings.text != blank -%}
          {{- section.settings.text -}}
        {%- endif -%}
      </div>
      {%- if section.settings.swiper_navigation -%}
        <div class="default-swiper__arrows is-flex">
          <button class="swiper-button-prev {{ nav_prev_el }} is-flex is-justified-center" aria-label="{% render "format-translation", namespace: "accessibility", key: "previous_slide", fallback: "Previous slide" %}">
            {%- render 'global-icon', icon: 'caret-left', icon_size: 'xs' -%}
          </button>
          <button class="swiper-button-next {{ nav_next_el }} is-flex is-justified-center" aria-label="{% render "format-translation", namespace: "accessibility", key: "next_slide", fallback: "Next slide" %}">
            {%- render 'global-icon', icon: 'caret-right', icon_size: 'xs' -%}
          </button>
        </div>
      {%- endif -%}
    </div>
    <div class="default-swiper__swiper swiper-container swiper-container--2-slides swiper-container--md-3-slides swiper-container--lg-4-slides" data-default-swiper>
      <div class="swiper-wrapper">
        Slides go here
      </div>
    </div>
  </div>

  Navigation with pagination (fractions)
  {%- comment -%} Swiper navigation + pagination {%- endcomment -%}
  {%- if section.settings.swiper_navigation -%}
    <div class="default-swiper__nav is-flex has-gap-16">
      <button class="swiper-navigation-prev-{{ section.id }} button button--link" data-swiper-nav="prev" aria-label="{% render "format-translation", namespace: "accessibility.previous_slide", fallback: "Previous" %}">
        {% render 'global-icon', icon: 'caret-left' %}
      </button>
      {%- if section.settings.swiper_pagination -%}
        <div class="collection-detail-slide__pagination is-flex is-justified-center"></div>
      {%- endif -%}
      <button class="swiper-navigation-next-{{ section.id }} button button--link" data-swiper-nav="next" aria-label="{% render "format-translation", namespace: "accessibility.next_slide", fallback: "Next" %}">
        {% render 'global-icon', icon: 'caret-right' %}
      </button>
    </div>
  {%- endif -%}

  {%- comment -%} Swiper pagination progress bar {%- endcomment -%}
  {%- if section.settings.swiper_pagination -%}
    <div class="default-swiper__nav is-flex has-gap-16">
      <div class="default-swiper__pagination default-swiper__pagination-{{ section.id }} is-flex is-justified-center"></div>
    </div>
  {%- endif -%}

  Add data-options per section, like for example:
    data-options='{
      "sectionID": "{{ section.id }}",
      "slidesPerView": 1,
      "slidesPerGroup": 1,
      "speed": {{ section.settings.swiper_speed }},
      "effect":	"{{ section.settings.swiper_effect }}",
      "loop":	{{ section.settings.swiper_loop }},
      {% if section.settings.swiper_autoplay %}
      "autoplay": {
        "delay": {{ section.settings.swiper_autoplay_speed }}
      },
      {% endif %}
      {% if section.settings.swiper_navigation %}
      "navigation": {
        "nextEl": ".swiper-button-next-{{ section.id }}",
        "prevEl": ".swiper-button-prev-{{ section.id }}"
      },
      {% endif %}
      {% if section.settings.swiper_pagination %}
        "pagination": {
          "el": ".swiper-pagination-{{ section.id }}",
          "clickable": true
        },
      {% endif %}
      {% if section.settings.swiper_scrollbar %}
        "scrollbar": {
          "el": ".swiper-scrollbar-{{ section.id }}",
          "draggable": true
        },
      {% endif %}
      {%- if section.settings.fade_effect %}
        "effect": "fade",
        "fadeEffect": {
          "crossFade": true
        },
      {%- endif %}
      "spaceBetween": 10,
      "breakpoints": {
        "375": {
          "slidesPerGroup": 2,
          "slidesPerView": 2
        },
        "768": {
          "slidesPerGroup": 3,
          "slidesPerView": 3.2
        },
        "1024": {
          "slidesPerGroup": 4,
          "slidesPerView": 4,
          "spaceBetween": 30
        },
        "1441": {
          "slidesPerGroup": 4,
          "slidesPerView": 4.2,
          "spaceBetween": 30
        }
      }
    }'
 **/

(async () => {
  if (!customElements.get('default-swiper')) {
    const { Swiper } = await import('swiper');
    const { Navigation, A11y, Pagination, Scrollbar, Autoplay, EffectFade } = await import('swiper/modules');

    Swiper.use([Navigation, A11y, Pagination, Scrollbar, Autoplay, EffectFade]);

    class DefaultSwiper extends HTMLElement {
      constructor() {
        super();

        this.ww = window.innerWidth;

        this.swiperElement = this.querySelector('[data-default-swiper]');
        this.swiperOptions = {
          a11y: true,
          observer: true,
          threshold: 10,
          slidesPerView: 'auto',
          destroyOnDesktop: false, // not a swiper option, but added for sub-collection-slider. In the settings of the slider, add the breakpoint size
          showRecentProducts: false, // not a swiper option, but added for 'recently viewed products' - requires the below option
          totalSlides: 8, // not a swiper option, but added for 'recently viewed products' which is based on a an array of visited products
          isProductRecommendations: false, // not a swiper option, but added for product recommendations on PDP
        };

        // Check if we have extra options on the HTML
        if (this && this.dataset && this.dataset.options) {
          const jsonOptions = JSON.parse(this.dataset.options);
          if (jsonOptions) {
            this.swiperOptions = {
              ...this.swiperOptions,
              ...jsonOptions,
            };
          }
        }

        if (this.swiperOptions.showRecentProducts) {
          // if this should be the swiper for 'recently viewed items' but the local storage is empty, hide the swiper
          if (window.localStorage.getItem('visited-products') === null) {
            this.closest('.product-carousel-section').classList.add('hidden');
          } else {
            // Render the 'recently viewed items'
            this.getProducts();
            this.classList.remove('hidden');
          }
        } else if (this.swiperOptions.isProductRecommendations) {
          // if this is the swiper for product recommendations
          this.getRecommendations();
        } else {
          // don't init when there is an upsell swiper
          if (this.querySelector('product-upsell-swiper') && this.swiperInstance) {
            return;
          }
          // default init
          this.init();
        }

        // destroy when ww is > desktop
        if (this.swiperOptions.destroyOnDesktop !== false && this.ww >= this.swiperOptions.destroyOnDesktop) {
          this.swiperInstance.destroy();
        }

        this.debouncedOnResize = debounce(() => {
          const windowWidth = window.innerWidth;

          // check if there's an actual change in window width, because the resize is triggered on mobile when the viewport changes with the header
          if (windowWidth === this.ww) return;
          this.ww = windowWidth;

          this.updateSwipers();
        }, 100);
        window.addEventListener('resize', this.debouncedOnResize.bind(this));

        // custom event only triggered on the PDP. We need this to re-init the swipers because they get lost when the content is re-rendered.
        window.addEventListener('content-placed', (event) => {
          const productSwipers = event.target.querySelector('product-content').querySelectorAll('[data-swiper-main]');
          productSwipers.forEach((productSwiper) => {
            productSwiper.init();
          });
        });

        // update swipers when Theme Editor is active
        window.addEventListener('shopify:section:load', () => {
          this.updateSwipers();
        });
      }

      /**
       *
       * @param {*} element is added by product-upsell-swiper since that content is fetched and doesn't exist
       */
      init(element = this.swiperElement) {
        this.swiperInstance = new Swiper(element, this.swiperOptions);

        this.querySelector('.cart-notification-upsell')?.classList.add('hidden');
      }

      update() {
        // called by Instagram
        if (this.swiperInstance) {
          this.swiperInstance.update();
        }
      }

      updateSwipers() {
        // Check if there is a swiper instance, else init it
        if (this.swiperInstance) {
          // Destroy logic - used on collection page for sub collection nav
          if (this.swiperOptions.destroyOnDesktop !== false) {
            if (this.ww >= this.swiperOptions.destroyOnDesktop) {
              this.swiperInstance.destroy();
            } else if (this.swiperInstance.destroyed) {
              this.init();
            }
          }
        } else {
          this.init();
        }
      }

      /**
       *
       * @returns the recently viewed products
       */
      async getProducts() {
        this.productsArray = JSON.parse(window.localStorage.getItem('visited-products')).reverse();

        // cut the array to the max amount of slides
        if (this.productsArray.length > this.swiperOptions.totalSlides) {
          this.productsArray.length = this.swiperOptions.totalSlides;
        }
        this.querySelector('[data-product-card-wrapper]').innerHTML = '';

        // use map instead of forEach because async/await won't work. Product string includes /product/ or even collections/collection_handle/product/
        const promises = this.productsArray.map(async (product) => {
          await fetch(
            `${product}?view=product-card&color-scheme=${this.swiperOptions.colorScheme}&image-max-width=${this.swiperOptions.imageMaxWidth}&image-sizes=${this.swiperOptions.imageSizes}`,
          )
            .then((response) => response.text())
            .then((responseText) => {
              const parsedHTML = new DOMParser().parseFromString(responseText, 'text/html');
              const productHTML = parsedHTML.getElementById('product-card-slide');

              if (productHTML) {
                // appending the slide will automatically trigger the class and init the product card swiper
                this.querySelector('[data-product-card-wrapper]').append(productHTML);
              }
            })
            .catch((error) => {
              debug() && console.log(error);
            });
        });

        // init the main swiper when all items have been processed
        await Promise.all(promises).then(() => {
          this.swiperInstance = new Swiper(this.swiperElement, this.swiperOptions);
          this.swiperInstance.update();

          // update the reviews
          if (window.apps.reviewsIo) getRating();
        });
      }

      /**
       *
       * @returns the product recommendations and adds them to the swiper
       */
      async getRecommendations() {
        if (this.dataset.url === 'undefined') return;

        try {
          // create the new object to add the free gift to the cart
          const response = await fetch(this.dataset.url);

          if (!response.ok) throw new Error(response.status);

          // On success:
          debug() && console.log('Recommendations fetched successfully!');
          const responseText = await response.text();
          const html = new DOMParser().parseFromString(responseText, 'text/html');
          const source = html.querySelector('[data-recommended-products]');
          const target = this.querySelector('[data-recommended-products]');

          if (source && target) {
            target.innerHTML = source.innerHTML;
            this.swiperInstance = new Swiper(this.swiperElement, this.swiperOptions);
          }
        } catch (error) {
          debug() && console.log('Error fetching product recommendations.', error);
        } finally {
          // still init the swiper with default products
          this.swiperInstance = new Swiper(this.swiperElement, this.swiperOptions);

          // update the reviews
          if (window.apps.reviewsIo) getRating();
        }
      }
    }
    customElements.define('default-swiper', DefaultSwiper);
    window.DefaultSwiper = DefaultSwiper;
  }
})();
